import React from "react";
import Icon from "components/AppIcon";
import { useConfig } from "../../../context/ConfigContext";

const LooptFoundationForm = ({
  formData,
  errors,
  onInputChange,
  isValidating,
}) => {
  const { config } = useConfig();
  const handleCogsIncrement = (increment) => {
    const currentCogs = parseFloat(formData.looptCogsPerUnit) || 0;
    const newCogs = Math.max(0, currentCogs + increment);
    onInputChange("looptCogsPerUnit", newCogs.toString());
  };

  const handleTaxRateIncrement = (increment) => {
    const currentRate = parseFloat(formData.looptCorporateTaxRate) || 0;
    const newRate = Math.max(0, Math.min(100, currentRate + increment));
    onInputChange("looptCorporateTaxRate", newRate.toString());
  };

  // Remove restrictive currency formatting - allow free typing
  // Currency symbol will be shown as prefix, but input allows normal number entry

  // Read-only fields carried from previous inputs
  const readOnlyFields = [
    {
      name: "units",
      label: "Units",
      icon: "Hash",
      helpText: "Total number of units",
    },
    {
      name: "materialWeight",
      label: "Material Weight (LBS)",
      icon: "Weight",
      helpText: "Total weight of material in pounds",
    },
  ];

  // Input fields for this step
  const inputFields = [
    {
      name: "looptCogsPerUnit",
      label: "COGS Per Unit",
      type: "number",
      placeholder: config?.default_cogs_placeholder?.toString() || "8.00",
      icon: "DollarSign",
      helpText: "Cost of Goods Sold per individual unit",
      min: "0",
      step: "0.01",
      hasIncrementControls: true,
      isCurrency: true, // Flag to show currency symbol
    },
    {
      name: "looptCorporateTaxRate",
      label: "Corporate Tax Rate (%)",
      type: "percentage",
      placeholder: "20",
      icon: "Receipt",
      helpText: "Corporate tax rate as a percentage (0-100)",
      min: "0",
      max: "100",
      step: "0.1",
      hasIncrementControls: true,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-2">
          Donation Model Inputs
        </h3>
        <p className="text-sm text-text-secondary">
          Enhanced specifications for sustainable textile processing with COGS
          and tax calculations
        </p>
      </div>

      <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
        {/* Read-only fields from previous inputs */}
        {readOnlyFields.map((field) => (
          <div key={field.name} className="space-y-2">
            <label
              htmlFor={field.name}
              className="block text-sm font-body-medium text-text-primary"
            >
              <div className="flex items-center space-x-2">
                <Icon name={field.icon} size={16} className="text-success" />
                <span>{field.label}</span>
              </div>
            </label>

            <div className="relative">
              <input
                id={field.name}
                type="text"
                value={formData[field.name] || ""}
                readOnly
                disabled
                className="block w-full px-4 py-3 border rounded-lg text-sm bg-secondary-100 text-text-secondary border-border cursor-not-allowed"
                aria-describedby={`${field.name}-help`}
              />
            </div>

            {/* Help Text */}
            <p id={`${field.name}-help`} className="text-xs text-text-tertiary">
              {field.helpText}
            </p>
          </div>
        ))}

        {/* Input fields for this step */}
        {inputFields.map((field) => (
          <div key={field.name} className="space-y-2">
            <label
              htmlFor={field.name}
              className="block text-sm font-body-medium text-text-primary"
            >
              <div className="flex items-center space-x-2">
                <Icon name={field.icon} size={16} className="text-success" />
                <span>{field.label}</span>
                <span className="text-error">*</span>
              </div>
            </label>

            <div className="relative">
              {field.type === "percentage" ? (
                <div className="relative">
                  <input
                    id={field.name}
                    type="number"
                    value={formData[field.name]}
                    onChange={(e) => onInputChange(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    className={`block w-full ${
                      field.hasIncrementControls ? "pr-20" : "pr-4"
                    } pl-8 py-3 border rounded-lg text-sm placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-success focus:border-success transition-all duration-150 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                      errors[field.name]
                        ? "border-error bg-error-50 text-error"
                        : "border-border bg-surface text-text-primary hover:border-success"
                    }`}
                    aria-describedby={`${field.name}-help ${field.name}-error`}
                    aria-invalid={!!errors[field.name]}
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-text-tertiary text-sm">%</span>
                  </div>
                  {field.hasIncrementControls && (
                    <div className="absolute inset-y-0 right-0 flex items-center">
                      <button
                        type="button"
                        onClick={() => handleTaxRateIncrement(-0.1)}
                        className="px-2 py-1 text-text-secondary hover:text-success focus:outline-none focus:text-success transition-colors duration-150"
                        aria-label="Decrease tax rate"
                      >
                        <Icon name="Minus" size={16} />
                      </button>
                      <div className="w-px h-6 bg-border mx-1"></div>
                      <button
                        type="button"
                        onClick={() => handleTaxRateIncrement(0.1)}
                        className="px-2 py-1 text-text-secondary hover:text-success focus:outline-none focus:text-success transition-colors duration-150"
                        aria-label="Increase tax rate"
                      >
                        <Icon name="Plus" size={16} />
                      </button>
                    </div>
                  )}
                </div>
              ) : field.hasIncrementControls ? (
                <div className="relative">
                  {/* Currency symbol for COGS field */}
                  {field.isCurrency && (
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-text-secondary text-sm">$</span>
                    </div>
                  )}
                  <input
                    id={field.name}
                    type={field.type}
                    value={formData[field.name]}
                    onChange={(e) => onInputChange(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    min={field.min}
                    step={field.step}
                    className={`block w-full pr-20 ${
                      field.isCurrency ? "pl-8" : "pl-4"
                    } py-3 border rounded-lg text-sm placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-success focus:border-success transition-all duration-150 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                      errors[field.name]
                        ? "border-error bg-error-50 text-error"
                        : "border-border bg-surface text-text-primary hover:border-success"
                    }`}
                    aria-describedby={`${field.name}-help ${field.name}-error`}
                    aria-invalid={!!errors[field.name]}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    <button
                      type="button"
                      onClick={() => {
                        if (field.name === "looptCogsPerUnit")
                          handleCogsIncrement(-1);
                        else if (field.name === "looptCorporateTaxRate")
                          handleTaxRateIncrement(-0.1);
                      }}
                      className="px-2 py-1 text-text-secondary hover:text-success focus:outline-none focus:text-success transition-colors duration-150"
                      aria-label={`Decrease ${field.label.toLowerCase()}`}
                    >
                      <Icon name="Minus" size={16} />
                    </button>
                    <div className="w-px h-6 bg-border mx-1"></div>
                    <button
                      type="button"
                      onClick={() => {
                        if (field.name === "looptCogsPerUnit")
                          handleCogsIncrement(1);
                        else if (field.name === "looptCorporateTaxRate")
                          handleTaxRateIncrement(0.1);
                      }}
                      className="px-2 py-1 text-text-secondary hover:text-success focus:outline-none focus:text-success transition-colors duration-150"
                      aria-label={`Increase ${field.label.toLowerCase()}`}
                    >
                      <Icon name="Plus" size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <input
                  id={field.name}
                  type={field.type}
                  value={formData[field.name]}
                  onChange={(e) => onInputChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  min={field.min}
                  step={field.step}
                  className={`block w-full px-4 py-3 border rounded-lg text-sm placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-success focus:border-success transition-all duration-150 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    errors[field.name]
                      ? "border-error bg-error-50 text-error"
                      : "border-border bg-surface text-text-primary hover:border-success"
                  }`}
                  aria-describedby={`${field.name}-help ${field.name}-error`}
                  aria-invalid={!!errors[field.name]}
                />
              )}

              {/* Loading indicator for validation */}
              {isValidating && formData[field.name] && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <div className="animate-spin">
                    <Icon name="Loader2" size={16} className="text-success" />
                  </div>
                </div>
              )}
            </div>

            {/* Help Text */}
            <p id={`${field.name}-help`} className="text-xs text-text-tertiary">
              {field.helpText}
            </p>

            {/* Error Message */}
            {errors[field.name] && (
              <div
                id={`${field.name}-error`}
                className="flex items-center space-x-2 text-sm text-error"
                role="alert"
              >
                <Icon name="AlertCircle" size={16} />
                <span>{errors[field.name]}</span>
              </div>
            )}
          </div>
        ))}
      </form>
    </div>
  );
};

export default LooptFoundationForm;
